import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Play, Upload, Trash2, Edit2, Save, X, Settings, 
  ToggleLeft, ToggleRight, Eye, MousePointer, Clock,
  Percent, Film, Loader2, ExternalLink, BarChart3, Plus,
  Video, Target, ChevronRight, Search, AlertCircle, 
  DollarSign, Link2, Globe, Check, RotateCcw, Copy
} from 'lucide-react';
import axios from 'axios';
import AdAnalytics from './AdAnalytics';
import AdCuePointEditor from './AdCuePointEditor';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Combined Ad Management Component
 * Combines:
 * - Tab 1: Global Settings (ad breaks, VAST tag, rotation)
 * - Tab 2: Your Video Ads (upload/manage custom ads)
 * - Tab 3: Video Ad Cue Points (per-video ad placement)
 * - Tab 4: Analytics
 */
const AdManagementCombined = ({ authToken }) => {
  // Main tab state
  const [activeTab, setActiveTab] = useState('settings'); // 'settings' | 'ads' | 'cue-points' | 'analytics'
  
  // ========== GLOBAL SETTINGS STATE ==========
  const [settings, setSettings] = useState({
    custom_ads_enabled: true,
    rotation_percentage: 50,
    google_vast_tag: '',
    ad_break_1_enabled: true,
    ad_break_1_time: 0,
    ad_break_1_ad_id: '',
    ad_break_2_enabled: false,
    ad_break_2_time: 120,
    ad_break_2_ad_id: '',
    ad_break_3_enabled: false,
    ad_break_3_time: 300,
    ad_break_3_ad_id: '',
    ad_break_4_enabled: false,
    ad_break_4_time: 600,
    ad_break_4_ad_id: '',
    ad_break_5_enabled: false,
    ad_break_5_time: 900,
    ad_break_5_ad_id: '',
    ad_break_6_enabled: false,
    ad_break_6_time: -1,
    ad_break_6_ad_id: ''
  });
  
  // ========== ADS STATE ==========
  const [ads, setAds] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [previewAd, setPreviewAd] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    video_url: '',
    thumbnail_url: '',
    duration: '',
    skip_after: 5,
    click_url: '',
    cta_text: 'Learn More',
    cta_enabled: true,
    is_active: true
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMode, setUploadMode] = useState('url'); // 'file' | 'url'
  const fileInputRef = useRef(null);
  
  // ========== CUE POINTS STATE ==========
  const [videos, setVideos] = useState([]);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // ========== GENERAL STATE ==========
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // ========== VAST TAG STATE ==========
  const [vastTagInput, setVastTagInput] = useState('');
  const [vastTagSaving, setVastTagSaving] = useState(false);
  const [vastTagStatus, setVastTagStatus] = useState('idle'); // 'idle', 'saved', 'error'
  const [vastCopied, setVastCopied] = useState(false);
  const [vastTestResult, setVastTestResult] = useState(null); // { valid, message }
  const [vastTesting, setVastTesting] = useState(false);
  const DEFAULT_VAST_TAG = 'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_preroll_skippable&sz=1920x1080|1280x720|640x480&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=';
  
  // ========== FETCH DATA ==========
  useEffect(() => {
    fetchAllData();
  }, [authToken]);
  
  const fetchAllData = async (retryCount = 0) => {
    setLoading(true);
    try {
      const token = authToken || localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
      if (!token) {
        setLoading(false);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const [adsRes, settingsRes, videosRes] = await Promise.all([
        axios.get(`${API}/api/vod/admin/ads`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/vod/admin/ads/settings`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/vod-admin/ad-cues/videos-with-ads`, { headers, timeout: 15000 })
      ]);
      setAds(adsRes.data || []);
      setSettings(settingsRes.data || settings);
      setVastTagInput(settingsRes.data?.google_vast_tag || '');
      setVideos(videosRes.data.videos || []);
    } catch (err) {
      console.error('Error fetching ad data:', err);
      if (retryCount < 2) {
        console.log(`[AdManagement] Retrying fetch (attempt ${retryCount + 2})...`);
        setTimeout(() => fetchAllData(retryCount + 1), 1500);
        return;
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Helper to get auth headers with fallback
  const getAuthHeaders = () => {
    const token = authToken || localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
    return { Authorization: `Bearer ${token}` };
  };

  // ========== SETTINGS FUNCTIONS ==========
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/vod/admin/ads/settings`, settings, {
        headers: getAuthHeaders()
      });
      alert('Settings saved successfully!');
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  // ========== VAST TAG FUNCTIONS ==========
  const handleSaveVastTag = async () => {
    if (vastTagInput && !vastTagInput.startsWith('http')) {
      setVastTagStatus('error');
      return;
    }
    setVastTagSaving(true);
    setVastTagStatus('idle');
    try {
      await axios.put(`${API}/api/vod/admin/ads/settings`, {
        google_vast_tag: vastTagInput || ''
      }, {
        headers: getAuthHeaders()
      });
      setSettings(prev => ({ ...prev, google_vast_tag: vastTagInput }));
      setVastTagStatus('saved');
      setTimeout(() => setVastTagStatus('idle'), 3000);
    } catch (err) {
      console.error('Error saving VAST tag:', err);
      setVastTagStatus('error');
    } finally {
      setVastTagSaving(false);
    }
  };

  const handleResetVastTag = async () => {
    if (!confirm('Reset to default Google test ads? Your custom VAST tag will be removed.')) return;
    setVastTagSaving(true);
    try {
      await axios.put(`${API}/api/vod/admin/ads/settings`, {
        google_vast_tag: ''
      }, {
        headers: getAuthHeaders()
      });
      setVastTagInput('');
      setSettings(prev => ({ ...prev, google_vast_tag: '' }));
      setVastTagStatus('saved');
      setTimeout(() => setVastTagStatus('idle'), 3000);
    } catch (err) {
      console.error('Error resetting VAST tag:', err);
      setVastTagStatus('error');
    } finally {
      setVastTagSaving(false);
    }
  };

  const handleCopyVastTag = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setVastCopied(true);
      setTimeout(() => setVastCopied(false), 2000);
    });
  };

  const handleTestVastTag = async () => {
    const urlToTest = vastTagInput || settings.google_vast_tag;
    if (!urlToTest) {
      setVastTestResult({ valid: false, message: 'Enter a VAST tag URL first.' });
      return;
    }
    if (!urlToTest.startsWith('http')) {
      setVastTestResult({ valid: false, message: 'URL must start with http:// or https://' });
      return;
    }
    setVastTesting(true);
    setVastTestResult(null);
    try {
      const res = await axios.post(`${API}/api/vod/admin/ads/test-vast-tag`, {
        vast_url: urlToTest
      }, {
        headers: getAuthHeaders()
      });
      setVastTestResult(res.data);
    } catch (err) {
      setVastTestResult({ valid: false, message: 'Failed to test tag. Server error.' });
    } finally {
      setVastTesting(false);
    }
  };
  
  const getEnabledAdBreaksCount = () => {
    let count = 0;
    for (let i = 1; i <= 6; i++) {
      if (settings[`ad_break_${i}_enabled`]) count++;
    }
    return count;
  };
  
  // ========== ADS FUNCTIONS ==========
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      alert('Please select a video file');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      alert('File too large. Maximum size is 100MB.');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('quality', 'ads');
      uploadFormData.append('video_name', formData.name || formData.title || '');
      
      const uploadRes = await axios.post(`${API}/api/r2/simple-upload`, uploadFormData, {
        headers: getAuthHeaders(),
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      
      if (!uploadRes.data.url) throw new Error('No URL returned from upload');
      
      setFormData(prev => ({ ...prev, video_url: uploadRes.data.url }));
      
      // Get video duration
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        setFormData(prev => ({ ...prev, duration: Math.round(video.duration) }));
      };
      video.src = uploadRes.data.url;
    } catch (err) {
      console.error('Upload error:', err);
      alert(`Failed to upload video: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  const handleSubmitAd = async () => {
    if (!formData.title || !formData.video_url) {
      alert('Please provide a title and upload a video');
      return;
    }
    
    setSaving(true);
    try {
      if (editingAd) {
        await axios.put(`${API}/api/vod/admin/ads/${editingAd.id}`, formData, {
          headers: getAuthHeaders()
        });
      } else {
        await axios.post(`${API}/api/vod/admin/ads`, formData, {
          headers: getAuthHeaders()
        });
      }
      
      fetchAllData();
      setShowAddModal(false);
      setEditingAd(null);
      resetForm();
    } catch (err) {
      console.error('Error saving ad:', err);
      alert('Failed to save ad');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteAd = async (adId) => {
    if (!confirm('Are you sure you want to delete this ad?')) return;
    try {
      await axios.delete(`${API}/api/vod/admin/ads/${adId}`, {
        headers: getAuthHeaders()
      });
      fetchAllData();
    } catch (err) {
      console.error('Error deleting ad:', err);
      alert('Failed to delete ad');
    }
  };
  
  const handleEditAd = (ad) => {
    setEditingAd(ad);
    setFormData({
      title: ad.title,
      video_url: ad.video_url,
      thumbnail_url: ad.thumbnail_url || '',
      duration: ad.duration || '',
      skip_after: ad.skip_after != null ? ad.skip_after : 5,
      click_url: ad.click_url || '',
      cta_text: ad.cta_text || 'Learn More',
      cta_enabled: ad.cta_enabled !== false,
      is_active: ad.is_active
    });
    setShowAddModal(true);
  };
  
  const toggleAdStatus = async (ad) => {
    try {
      await axios.put(`${API}/api/vod/admin/ads/${ad.id}`, { is_active: !ad.is_active }, {
        headers: getAuthHeaders()
      });
      fetchAllData();
    } catch (err) {
      console.error('Error toggling ad status:', err);
    }
  };
  
  const resetForm = () => {
    setFormData({
      title: '',
      video_url: '',
      thumbnail_url: '',
      duration: '',
      skip_after: 5,
      click_url: '',
      cta_text: 'Learn More',
      cta_enabled: true,
      is_active: true
    });
    setUploadMode('url');
  };
  
  // ========== CUE POINTS FUNCTIONS ==========
  const filteredVideos = videos.filter(v => 
    v.video?.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // If editing a specific video's cue points, show the editor
  if (selectedVideoId) {
    return (
      <AdCuePointEditor 
        authToken={authToken} 
        videoId={selectedVideoId}
        onBack={() => {
          setSelectedVideoId(null);
          fetchAllData();
        }}
      />
    );
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-4">
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'settings'
              ? 'bg-purple-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
          data-testid="settings-tab"
        >
          <Settings className="h-4 w-4 mr-2" />
          Global Settings
        </button>
        <button
          onClick={() => setActiveTab('ads')}
          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'ads'
              ? 'bg-purple-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
          data-testid="ads-tab"
        >
          <Film className="h-4 w-4 mr-2" />
          Your Video Ads
          <Badge className="ml-2 bg-purple-500/20 text-purple-300">{ads.length}</Badge>
        </button>
        <button
          onClick={() => setActiveTab('cue-points')}
          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'cue-points'
              ? 'bg-yellow-600 text-black'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
          data-testid="cue-points-tab"
        >
          <Target className="h-4 w-4 mr-2" />
          Video Ad Cue Points
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'analytics'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
          data-testid="analytics-tab"
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          Analytics
        </button>
        <button
          onClick={() => setActiveTab('vast')}
          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'vast'
              ? 'bg-green-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
          data-testid="vast-tags-tab"
        >
          <Globe className="h-4 w-4 mr-2" />
          VAST Tags
        </button>
      </div>
      
      {/* ============ GLOBAL SETTINGS TAB ============ */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Settings className="h-6 w-6 text-purple-400" />
                <h2 className="text-xl font-bold">Global Ad Settings</h2>
              </div>
              <Button
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-500"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Settings
              </Button>
            </div>
            
            {/* Custom Ads Toggle & Rotation */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <label className="text-sm text-gray-400 block mb-3">Custom Ads</label>
                <button
                  onClick={() => setSettings(prev => ({ ...prev, custom_ads_enabled: !prev.custom_ads_enabled }))}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all w-full ${
                    settings.custom_ads_enabled 
                      ? 'bg-green-500/20 border-green-500/50 text-green-400' 
                      : 'bg-gray-800 border-white/10 text-gray-400'
                  }`}
                >
                  {settings.custom_ads_enabled ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
                  <span className="font-medium">{settings.custom_ads_enabled ? 'Enabled' : 'Disabled'}</span>
                </button>
              </div>
              
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <label className="text-sm text-gray-400 block mb-3">
                  Ad Source Mix: <span className="text-white font-bold">{settings.rotation_percentage}% Custom Ads / {100 - settings.rotation_percentage}% VAST Network</span>
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-purple-400 w-24">Custom Ads</span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={settings.rotation_percentage}
                      onChange={(e) => setSettings(prev => ({ ...prev, rotation_percentage: parseInt(e.target.value) }))}
                      className="flex-1 accent-purple-500 h-2"
                    />
                    <span className="text-sm text-blue-400 w-24">VAST Network</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Ad Breaks */}
            <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Clock className="h-5 w-5 text-purple-400" />
                    Default Ad Placements ({getEnabledAdBreaksCount()} Active)
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">These apply to videos without custom cue points</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Ad Break Cards */}
                {[1, 2, 3, 4, 5, 6].map((i) => {
                  const enabled = settings[`ad_break_${i}_enabled`];
                  const time = settings[`ad_break_${i}_time`];
                  const isPreRoll = i === 1;
                  const isPostRoll = i === 6;
                  const label = isPreRoll ? 'PRE-ROLL' : isPostRoll ? 'POST-ROLL' : 'MID-ROLL';
                  const color = isPreRoll ? 'purple' : isPostRoll ? 'orange' : 'blue';
                  
                  return (
                    <div key={i} className={`p-4 rounded-xl border transition-all ${
                      enabled ? 'bg-green-500/10 border-green-500/40' : 'bg-gray-900/50 border-gray-700'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-white">Ad Break {i}</span>
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, [`ad_break_${i}_enabled`]: !enabled }))}
                          className="focus:outline-none"
                        >
                          {enabled ? <ToggleRight className="h-6 w-6 text-green-400" /> : <ToggleLeft className="h-6 w-6 text-gray-500" />}
                        </button>
                      </div>
                      <div className={`px-2 py-1 bg-${color}-500/20 rounded text-xs text-${color}-300 font-medium inline-block mb-2`}>
                        {label}
                      </div>
                      {!isPreRoll && !isPostRoll && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-400">At:</span>
                          <input
                            type="number"
                            min="1"
                            max="120"
                            value={Math.floor(time / 60)}
                            onChange={(e) => setSettings(prev => ({ ...prev, [`ad_break_${i}_time`]: parseInt(e.target.value) * 60 }))}
                            className="w-16 px-2 py-1.5 bg-black/50 border border-white/20 rounded text-white text-sm text-center"
                            disabled={!enabled}
                          />
                          <span className="text-xs text-gray-400">min</span>
                        </div>
                      )}
                      {isPreRoll && <p className="text-xs text-gray-400">Plays before video starts</p>}
                      {isPostRoll && <p className="text-xs text-gray-400">Plays after video ends</p>}
                      
                      {/* Ad Selection */}
                      <div className="mt-3 pt-3 border-t border-gray-700">
                        <label className="text-xs text-gray-400 block mb-1">Select Ad</label>
                        <select
                          value={settings[`ad_break_${i}_ad_id`] || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, [`ad_break_${i}_ad_id`]: e.target.value }))}
                          disabled={!enabled}
                          className="w-full px-2 py-1.5 bg-black/50 border border-white/20 rounded text-white text-xs disabled:opacity-50"
                        >
                          <option value="">Random (Any Active Ad)</option>
                          {ads.filter(ad => ad.is_active).map(ad => (
                            <option key={ad.id} value={ad.id}>{ad.title}</option>
                          ))}
                          <option value="google">VAST Network Ads Only</option>
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* ============ YOUR VIDEO ADS TAB ============ */}
      {activeTab === 'ads' && (
        <div className="space-y-6">
          <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Film className="h-6 w-6 text-purple-400" />
                <h2 className="text-xl font-bold">Your Video Ads</h2>
                <Badge className="bg-purple-500/20 text-purple-400">{ads.length} ads</Badge>
              </div>
              
              <Button
                onClick={() => {
                  setEditingAd(null);
                  resetForm();
                  setShowAddModal(true);
                }}
                className="bg-purple-600 hover:bg-purple-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Ad
              </Button>
            </div>
            
            {ads.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Film className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>No custom ads yet</p>
                <p className="text-sm mt-2">Upload your first video ad to get started</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ads.map((ad) => (
                  <div
                    key={ad.id}
                    className={`bg-black/30 rounded-xl border overflow-hidden transition-all ${
                      ad.is_active ? 'border-green-500/30' : 'border-white/10 opacity-60'
                    }`}
                  >
                    <div 
                      className="aspect-video bg-gray-900 relative cursor-pointer group"
                      onClick={() => setPreviewAd(ad)}
                    >
                      {ad.thumbnail_url ? (
                        <img src={ad.thumbnail_url} alt={ad.title} className="w-full h-full object-cover" />
                      ) : (
                        <video src={ad.video_url} className="w-full h-full object-cover" />
                      )}
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Play className="h-12 w-12 text-white" />
                      </div>
                      <div className="absolute top-2 right-2">
                        <Badge className={ad.is_active ? 'bg-green-500' : 'bg-gray-600'}>
                          {ad.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      {ad.duration && (
                        <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs">
                          {Math.floor(ad.duration / 60)}:{(ad.duration % 60).toString().padStart(2, '0')}
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4">
                      <h3 className="font-semibold truncate mb-2">{ad.title}</h3>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                        <span className="flex items-center gap-1">
                          <Eye className="h-4 w-4" />
                          {ad.impressions || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <MousePointer className="h-4 w-4" />
                          {ad.clicks || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          Skip: {ad.skip_after}s
                        </span>
                      </div>
                      
                      {/* CTA Info */}
                      {ad.click_url && (
                        <div className="mb-3 p-2 bg-purple-500/10 rounded-lg border border-purple-500/30">
                          <p className="text-xs text-purple-300">
                            CTA: {ad.cta_text || 'Learn More'} → {ad.click_url.substring(0, 30)}...
                          </p>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleAdStatus(ad)}
                          className="flex-1 border-white/20"
                        >
                          {ad.is_active ? 'Disable' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleEditAd(ad)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteAd(ad.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* ============ VIDEO AD CUE POINTS TAB ============ */}
      {activeTab === 'cue-points' && (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Target className="h-6 w-6 text-yellow-400" />
                Video Ad Cue Points
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Set custom ad placements for individual videos
              </p>
            </div>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search videos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-gray-800 border-gray-700 pl-10 w-64 text-white"
              />
            </div>
          </div>
          
          {/* Instructions */}
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4">
            <h3 className="font-medium text-yellow-300 mb-2">How it works</h3>
            <ol className="text-sm text-yellow-200/70 space-y-1 list-decimal list-inside">
              <li>Select a video from the list below</li>
              <li>Play the video and pause where you want an ad</li>
              <li>Click "Add Ad" to insert an ad cue point</li>
              <li>Choose between VAST network ads or your custom video ads</li>
            </ol>
          </div>
          
          {/* Videos List */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-800">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Video className="h-5 w-5 text-blue-400" />
                Your Videos
              </h3>
              <p className="text-xs text-gray-500 mt-1">{filteredVideos.length} videos</p>
            </div>
            
            <div className="divide-y divide-gray-800 max-h-[600px] overflow-y-auto">
              {filteredVideos.length === 0 ? (
                <div className="p-8 text-center">
                  <Video className="h-12 w-12 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-400">No videos found</p>
                </div>
              ) : (
                filteredVideos.map((item) => {
                  const { video, has_ad_settings, pre_roll, post_roll, mid_roll_count, mid_roll_mode } = item;
                  const hasAnyAds = pre_roll || post_roll || mid_roll_count > 0;
                  
                  return (
                    <div 
                      key={video.id}
                      className="p-4 hover:bg-gray-800/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedVideoId(video.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="relative flex-shrink-0">
                          {video.thumbnail_url ? (
                            <img
                              src={video.thumbnail_url}
                              alt={video.title}
                              className="w-32 h-20 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-32 h-20 rounded-lg bg-gray-800 flex items-center justify-center">
                              <Video className="h-8 w-8 text-gray-600" />
                            </div>
                          )}
                          <span className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                            {formatDuration(video.duration)}
                          </span>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white truncate">{video.title}</h4>
                          
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            {hasAnyAds ? (
                              <>
                                {pre_roll && (
                                  <Badge className="bg-green-600/20 text-green-400 border border-green-500/30">
                                    <Play className="h-3 w-3 mr-1" />
                                    Pre-Roll
                                  </Badge>
                                )}
                                {mid_roll_count > 0 && (
                                  <Badge className="bg-yellow-600/20 text-yellow-400 border border-yellow-500/30">
                                    <Target className="h-3 w-3 mr-1" />
                                    {mid_roll_count} Mid-Roll{mid_roll_count > 1 ? 's' : ''}
                                  </Badge>
                                )}
                                {post_roll && (
                                  <Badge className="bg-red-600/20 text-red-400 border border-red-500/30">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Post-Roll
                                  </Badge>
                                )}
                              </>
                            ) : (
                              <Badge className="bg-gray-700/50 text-gray-400 border border-gray-600/30">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                No Ads Configured
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <Button variant="ghost" className="text-gray-400 hover:text-white">
                          <span className="mr-2">{hasAnyAds ? 'Edit' : 'Configure'}</span>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* ============ ANALYTICS TAB ============ */}
      {activeTab === 'analytics' && (
        <AdAnalytics authToken={authToken} />
      )}
      
      {/* ============ VAST TAGS TAB ============ */}
      {activeTab === 'vast' && (
        <div className="space-y-6">
          {/* Active Tag Status */}
          <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <Globe className="h-6 w-6 text-purple-400" />
              <h2 className="text-xl font-bold">VAST Ad Tag Configuration</h2>
            </div>
            
            <p className="text-sm text-gray-400 mb-6">
              Configure your VAST ad tag URL. This determines which ads are served when the ad rotation selects a VAST network ad instead of your custom video ads. Works with Google Ad Manager, or any VAST-compatible ad network.
            </p>

            {/* Current Status Card */}
            <div className={`rounded-xl p-5 border mb-6 ${
              settings.google_vast_tag 
                ? 'bg-green-500/10 border-green-500/30' 
                : 'bg-blue-500/10 border-blue-500/30'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {settings.google_vast_tag ? (
                  <>
                    <Check className="h-5 w-5 text-green-400" />
                    <span className="font-semibold text-green-400">Custom VAST Tag Active</span>
                  </>
                ) : (
                  <>
                    <Link2 className="h-5 w-5 text-blue-400" />
                    <span className="font-semibold text-blue-400">Using Default Test Ads</span>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-400">
                {settings.google_vast_tag 
                  ? 'Your custom VAST tag is being used for all network ad placements.'
                  : 'No custom tag configured. Google test ads are being served as placeholder.'}
              </p>
            </div>

            {/* Custom VAST Tag Input */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-300 block">Your VAST Tag URL</label>
              <input
                type="url"
                value={vastTagInput}
                onChange={(e) => {
                  setVastTagInput(e.target.value);
                  setVastTagStatus('idle');
                }}
                placeholder="https://pubads.g.doubleclick.net/gampad/ads?iu=/..."
                className={`w-full px-4 py-3 bg-black/30 border rounded-xl text-white placeholder:text-gray-600 text-sm font-mono ${
                  vastTagStatus === 'error' ? 'border-red-500/50' : 'border-white/10'
                }`}
                data-testid="vast-tag-url-input"
              />
              
              {vastTagStatus === 'error' && (
                <div className="flex items-center gap-2 text-red-400 text-xs">
                  <AlertCircle className="h-3 w-3" />
                  <span>Please enter a valid URL starting with http:// or https://</span>
                </div>
              )}
              
              {vastTagStatus === 'saved' && (
                <div className="flex items-center gap-2 text-green-400 text-xs" data-testid="vast-tag-save-success">
                  <Check className="h-3 w-3" />
                  <span>VAST tag saved successfully</span>
                </div>
              )}

              <div className="flex gap-3 flex-wrap">
                <Button
                  onClick={handleSaveVastTag}
                  disabled={vastTagSaving}
                  className="bg-purple-600 hover:bg-purple-500"
                  data-testid="save-vast-tag-button"
                >
                  {vastTagSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save VAST Tag
                </Button>
                
                <Button
                  onClick={handleTestVastTag}
                  disabled={vastTesting || (!vastTagInput && !settings.google_vast_tag)}
                  variant="outline"
                  className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10"
                  data-testid="test-vast-tag-button"
                >
                  {vastTesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                  {vastTesting ? 'Testing...' : 'Test VAST Tag'}
                </Button>
                
                {settings.google_vast_tag && (
                  <Button
                    onClick={handleResetVastTag}
                    disabled={vastTagSaving}
                    variant="outline"
                    className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                    data-testid="reset-vast-tag-button"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset to Default
                  </Button>
                )}
              </div>
              
              {/* Test Result */}
              {vastTestResult && (
                <div className={`rounded-xl p-4 border ${
                  vastTestResult.valid === true
                    ? 'bg-green-500/10 border-green-500/30' 
                    : vastTestResult.valid === null
                    ? 'bg-yellow-500/10 border-yellow-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`} data-testid="vast-test-result">
                  <div className="flex items-center gap-2 mb-1">
                    {vastTestResult.valid === true
                      ? <Check className="h-4 w-4 text-green-400" />
                      : vastTestResult.valid === null
                      ? <AlertCircle className="h-4 w-4 text-yellow-400" />
                      : <AlertCircle className="h-4 w-4 text-red-400" />
                    }
                    <span className={`font-semibold text-sm ${
                      vastTestResult.valid === true ? 'text-green-400' 
                      : vastTestResult.valid === null ? 'text-yellow-400'
                      : 'text-red-400'
                    }`}>
                      {vastTestResult.valid === true ? 'Tag Validated' 
                       : vastTestResult.valid === null ? 'Cannot Verify From Server'
                       : 'Validation Failed'}
                    </span>
                    {vastTestResult.status_code > 0 && (
                      <span className="text-xs text-gray-500 ml-auto">HTTP {vastTestResult.status_code}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">{vastTestResult.message}</p>
                </div>
              )}
            </div>
          </div>

          {/* Default Tag Reference */}
          <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <Link2 className="h-5 w-5 text-gray-400" />
              <h3 className="text-lg font-semibold">Default Test Tag</h3>
              <Badge className="bg-gray-700 text-gray-300 text-xs">Reference</Badge>
            </div>
            <p className="text-xs text-gray-400 mb-3">
              When no custom VAST tag is set, this Google test ad tag is used. It serves skippable test ads for development purposes.
            </p>
            <div className="bg-black/40 rounded-lg p-3 border border-white/5 flex items-start gap-2">
              <code className="text-xs text-gray-500 break-all flex-1 leading-relaxed font-mono">
                {DEFAULT_VAST_TAG}
              </code>
              <button
                onClick={() => handleCopyVastTag(DEFAULT_VAST_TAG)}
                className="text-gray-500 hover:text-white transition-colors p-1 flex-shrink-0"
                title="Copy to clipboard"
                data-testid="copy-default-vast-tag"
              >
                {vastCopied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h3 className="text-lg font-semibold mb-4">How VAST Tags Work</h3>
            <div className="space-y-3 text-sm text-gray-400">
              <div className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                <p>When a viewer watches a video, the player checks your <strong className="text-white">Ad Source Mix</strong> setting to decide between your custom ads and VAST network ads.</p>
              </div>
              <div className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                <p>If VAST network ads are selected, the player uses your <strong className="text-white">VAST Tag URL</strong> to request an ad from your ad network.</p>
              </div>
              <div className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                <p>The ad network returns a video ad (pre-roll, mid-roll, or post-roll) based on your configuration.</p>
              </div>
              <div className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                <p>To get your own VAST tag, sign up at <a href="https://admanager.google.com" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline">Google Ad Manager</a> and create an ad unit.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* ============ ADD/EDIT AD MODAL ============ */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-gray-900 rounded-2xl border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">{editingAd ? 'Edit Ad' : 'Upload New Ad'}</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Ad Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter ad title"
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white placeholder:text-gray-500"
                  />
                </div>
                
                {/* Video Upload / URL */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Video Ad *</label>
                  {formData.video_url ? (
                    <div className="relative">
                      <video src={formData.video_url} className="w-full aspect-video rounded-xl object-cover" controls />
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, video_url: '', duration: '' }))}
                        className="absolute top-2 right-2 bg-red-500 p-1 rounded-full"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Mode Toggle */}
                      <div className="flex rounded-lg overflow-hidden border border-white/10">
                        <button
                          type="button"
                          onClick={() => setUploadMode('url')}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${uploadMode === 'url' ? 'bg-purple-600 text-white' : 'bg-black/20 text-gray-400 hover:text-white'}`}
                          data-testid="ad-mode-url"
                        >
                          <Globe className="h-4 w-4" />
                          Paste URL
                        </button>
                        <button
                          type="button"
                          onClick={() => setUploadMode('file')}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${uploadMode === 'file' ? 'bg-purple-600 text-white' : 'bg-black/20 text-gray-400 hover:text-white'}`}
                          data-testid="ad-mode-file"
                        >
                          <Upload className="h-4 w-4" />
                          Upload File
                        </button>
                      </div>

                      {uploadMode === 'url' ? (
                        <div>
                          <input
                            type="url"
                            value={formData.video_url}
                            onChange={(e) => {
                              const url = e.target.value;
                              setFormData(prev => ({ ...prev, video_url: url }));
                              // Auto-detect duration when URL is pasted
                              if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                                const video = document.createElement('video');
                                video.preload = 'metadata';
                                video.onloadedmetadata = () => {
                                  if (video.duration && isFinite(video.duration)) {
                                    setFormData(prev => ({ ...prev, duration: Math.round(video.duration) }));
                                  }
                                };
                                video.onerror = () => {};
                                video.src = url;
                              }
                            }}
                            placeholder="https://your-r2-bucket.r2.dev/video-ad.mp4"
                            className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white placeholder:text-gray-500"
                            data-testid="ad-video-url-input"
                          />
                          <p className="text-xs text-gray-500 mt-1">Paste your Cloudflare R2 or any direct video URL</p>
                        </div>
                      ) : (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className={`border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-purple-500/50 transition-colors ${uploading ? 'pointer-events-none' : ''}`}
                        >
                          {uploading ? (
                            <div className="space-y-2">
                              <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-500" />
                              <p>Uploading... {uploadProgress}%</p>
                              <div className="w-full bg-gray-700 rounded-full h-2">
                                <div className="bg-purple-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                              </div>
                            </div>
                          ) : (
                            <>
                              <Upload className="h-8 w-8 mx-auto mb-2 text-gray-500" />
                              <p className="text-gray-400">Click to upload video ad</p>
                              <p className="text-xs text-gray-600 mt-1">MP4, WebM, MOV supported</p>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" accept="video/*" onChange={handleFileUpload} className="hidden" />
                </div>
                
                {/* Skip After */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Skip After (seconds)</label>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={formData.skip_after}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({ ...prev, skip_after: val === '' ? 0 : parseInt(val) }));
                    }}
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">Time before skip button appears (0 = not skippable)</p>
                </div>
                
                {/* Click URL (CTA) */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Click URL (Call-to-Action)</label>
                  <input
                    type="url"
                    value={formData.click_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, click_url: e.target.value }))}
                    placeholder="https://example.com"
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white placeholder:text-gray-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL to open when viewer clicks the CTA button</p>
                </div>
                
                {/* CTA Settings - Only show if click_url is provided */}
                {formData.click_url && (
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-purple-300">Call-to-Action Button</h4>
                        <p className="text-xs text-gray-400">Show a clickable button during ad playback</p>
                      </div>
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, cta_enabled: !prev.cta_enabled }))}
                        className={`p-2 rounded-lg transition-colors ${formData.cta_enabled ? 'bg-purple-500/30 text-purple-300' : 'bg-gray-700 text-gray-400'}`}
                      >
                        {formData.cta_enabled ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
                      </button>
                    </div>
                    
                    {formData.cta_enabled && (
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Button Text</label>
                        <input
                          type="text"
                          value={formData.cta_text}
                          onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                          placeholder="Learn More"
                          className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder:text-gray-500"
                        />
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-xs text-gray-500">Preview:</span>
                          <button className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
                            {formData.cta_text || 'Learn More'}
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Active Toggle */}
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-400">Active</span>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                    className={`p-2 rounded-lg transition-colors ${formData.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'}`}
                  >
                    {formData.is_active ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
                  </button>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1 border-white/20">
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitAd}
                  disabled={saving || !formData.title || !formData.video_url}
                  className="flex-1 bg-purple-600 hover:bg-purple-500"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  {editingAd ? 'Update Ad' : 'Save Ad'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Preview Modal */}
      {previewAd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <div className="relative w-full max-w-4xl">
            <button
              onClick={() => setPreviewAd(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300"
            >
              <X className="h-8 w-8" />
            </button>
            <video src={previewAd.video_url} className="w-full rounded-2xl" controls autoPlay />
            <div className="mt-4 text-center">
              <h3 className="text-xl font-bold">{previewAd.title}</h3>
              {previewAd.click_url && (
                <a 
                  href={previewAd.click_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-400 hover:text-purple-300 flex items-center justify-center gap-1 mt-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  {previewAd.click_url}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdManagementCombined;
